import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { getOpenState, hasOpeningHours } from '@/lib/restaurantHours';
import { fetchMarketplaceSellers, fetchVendorCategoryLabels, type MarketplaceSeller, type VendorCategoryLabels } from '@/lib/marketplaceCatalog';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';

const SELLER_SOCIALS: { key: keyof MarketplaceSeller; icon: string; label: string }[] = [
  { key: 'lienfacebook', icon: 'ri-facebook-circle-line', label: 'Facebook' },
  { key: 'lieninstagram', icon: 'ri-instagram-line', label: 'Instagram' },
  { key: 'lientiktok', icon: 'ri-tiktok-line', label: 'TikTok' },
  { key: 'lientwitter', icon: 'ri-twitter-x-line', label: 'X' },
  { key: 'lienlinkedin', icon: 'ri-linkedin-box-line', label: 'LinkedIn' },
  { key: 'lienyoutube', icon: 'ri-youtube-line', label: 'YouTube' },
];

// ─────────────────────────────────────────────────────────────
// Liste des vendeurs rattachés à la marketplace (table userwebsite).
// Utilisable sur le site public d'un marchand de type marketplace,
// y compris dans un gabarit de thème.
// ─────────────────────────────────────────────────────────────

export default function MarketplaceSellersTag({ limit }: { limit?: number }) {
  const { tenant } = useTenant();
  const [sellers, setSellers] = useState<MarketplaceSeller[]>([]);
  const [ratings, setRatings] = useState<Record<number, VendorRating>>({});
  const [catLabels, setCatLabels] = useState<Record<number, VendorCategoryLabels>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchMarketplaceSellers(Number(tenant.id));
        if (cancelled) return;
        setSellers(limit ? list.slice(0, limit) : list);
        const rated = await fetchVendorRatings(list.map((s) => s.userId));
        if (!cancelled) setRatings(rated);
        const labels = await fetchVendorCategoryLabels(Number(tenant.id), list.map((s) => s.userId));
        if (!cancelled) setCatLabels(labels);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenant?.id, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <i className="ri-error-warning-line text-2xl text-red-400 mb-2 block"></i>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (sellers.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-user-star-line text-xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">Aucun vendeur disponible</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
      {sellers.map((seller) => {
        const img = resolveMediaUrl(seller.image);
        const open = hasOpeningHours(seller.horairesJson) ? getOpenState(seller.horairesJson) : null;
        const socials = SELLER_SOCIALS
          .map((s) => ({ ...s, url: String(seller[s.key] || '').trim() }))
          .filter((s) => /^https?:\/\//i.test(s.url));
        return (
          <Link
            key={seller.userId}
            to={`/boutique/${seller.userId}`}
            className="block bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-background-300/60 transition-colors no-underline"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {img ? (
                  <img src={img} alt={seller.nomcommerce} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-bold text-foreground-400">
                    {seller.nomcommerce.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate">{seller.nomcommerce}</h3>
                  {open && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${open.open ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${open.open ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {open.open ? 'Ouvert' : 'Fermé'}
                    </span>
                  )}
                </div>
                {seller.ville && (
                  <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                    <i className="ri-map-pin-line text-xs"></i>
                    {seller.ville}{seller.pays ? `, ${seller.pays}` : ''}
                  </span>
                )}
                <div className="mt-0.5">
                  <VendorRatingBadge
                    average={ratings[seller.userId]?.average ?? 0}
                    count={ratings[seller.userId]?.count ?? 0}
                  />
                </div>
                {catLabels[seller.userId]?.souscategorieNom && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-semibold">
                    <i className="ri-price-tag-2-line text-[9px]"></i>
                    {catLabels[seller.userId].souscategorieNom}
                  </span>
                )}
                {open && open.todayLabel && (
                  <span className="text-[11px] text-foreground-400 flex items-center gap-1 mt-0.5">
                    <i className="ri-time-line text-xs"></i>
                    {open.todayLabel === 'Fermé' ? 'Fermé aujourd\'hui' : `Aujourd\'hui : ${open.todayLabel}`}
                  </span>
                )}
              </div>
            </div>
            {seller.description && (
              <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{seller.description}</p>
            )}
            <div className="flex items-center gap-3 text-[11px] text-foreground-400">
              {seller.telephone && (
                <span className="flex items-center gap-1">
                  <i className="ri-phone-line text-xs"></i>
                  {seller.telephone}
                </span>
              )}
              {seller.name && (
                <span className="flex items-center gap-1 truncate">
                  <i className="ri-user-line text-xs"></i>
                  {seller.name}
                </span>
              )}
            </div>
            {socials.length > 0 && (
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-background-200/60">
                {socials.map((s) => (
                  <span
                    key={s.key as string}
                    title={s.label}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-500"
                  >
                    <i className={`${s.icon} text-xs`}></i>
                  </span>
                ))}
              </div>
            )}
          </Link>
        );
      })}
    </div>
  );
}